/**
 * Buyer Handoffs API Service (Phase 5).
 *
 * Frontend client for /api/v1/buyer-handoffs/*. Mirrors the realtor /
 * leads / listings / portfolio services: re-shapes the backend's
 * `{success, data, message, error}` envelope into
 * `{success, error, errorCode, data}`.
 *
 * Public token endpoints don't require auth — apiClient still attaches
 * the Authorization header if present, but the backend ignores it on
 * those routes.
 */

import { apiClient } from "@/lib/api-client";
import { getOrCreateDeviceId, setDeviceId } from "@/lib/token-manager";

const API_BASE = "/api/v1";

export type HandoffStatus = "sent" | "clicked" | "onboarded" | "expired";

export interface BuyerHandoff {
	id: string;
	realtorId: string;
	listingId: string;
	buyerPhoneCountryCode: string;
	buyerPhone: string;
	handoffToken: string;
	status: HandoffStatus;
	sentAt: string;
	clickedAt: string | null;
	onboardedAt: string | null;
	expiresAt: string;
	reissueCount: number;
	buyerUserId: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface RealtorHandoffRow {
	handoff: BuyerHandoff;
	listing: {
		id: string;
		headline: string;
		addressPublic: string;
		city: string;
		locality: string;
	};
}

export interface PublicHandoffPreview {
	handoffId: string;
	listing: {
		id: string;
		headline: string;
		addressPublic: string;
		city: string;
		locality: string;
		bhk: number | null;
		propertyType: string;
		askingPrice: number;
		images: string[];
	};
	realtor: { name: string; agencyName: string | null };
	buyerPhoneMasked: string;
	expiresAt: string;
}

export interface CreateHandoffInput {
	listingId: string;
	buyerPhone: string;
	buyerPhoneCountryCode?: string;
}

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

function buildFailure<T>(error: unknown, fallback: string): ApiResult<T> {
	if (error == null) {
		return { success: false, error: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	if (!err.response) {
		return {
			success: false,
			error:
				err.code === "ECONNABORTED" || err.message?.includes("timeout")
					? "Request timed out. Check your connection and try again."
					: "Couldn't reach the server. Check your connection and try again.",
			errorCode: "NETWORK_ERROR",
		};
	}
	return {
		success: false,
		error: err.response.data?.message ?? fallback,
		errorCode: err.response.data?.error ?? null,
	};
}

// ----------------------------------------------------------------------
// Realtor-scoped
// ----------------------------------------------------------------------

export async function createHandoff(
	input: CreateHandoffInput,
): Promise<ApiResult<BuyerHandoff>> {
	try {
		const response = await apiClient.post<{
			data: { handoff: BuyerHandoff };
		}>(`${API_BASE}/buyer-handoffs`, input);
		return { success: true, data: response.data.data.handoff };
	} catch (error: unknown) {
		return buildFailure<BuyerHandoff>(error, "Failed to create handoff");
	}
}

export async function listMyHandoffs(): Promise<
	ApiResult<RealtorHandoffRow[]>
> {
	try {
		const response = await apiClient.get<{
			data: { handoffs: RealtorHandoffRow[] };
		}>(`${API_BASE}/buyer-handoffs/me`);
		return { success: true, data: response.data.data.handoffs };
	} catch (error: unknown) {
		return buildFailure<RealtorHandoffRow[]>(error, "Failed to load handoffs");
	}
}

export async function reissueHandoff(
	handoffId: string,
): Promise<ApiResult<BuyerHandoff>> {
	try {
		const response = await apiClient.post<{
			data: { handoff: BuyerHandoff };
		}>(`${API_BASE}/buyer-handoffs/${handoffId}/reissue`);
		return { success: true, data: response.data.data.handoff };
	} catch (error: unknown) {
		return buildFailure<BuyerHandoff>(error, "Failed to reissue handoff");
	}
}

// ----------------------------------------------------------------------
// Public (token)
// ----------------------------------------------------------------------

export type PublicHandoffResult =
	| { success: true; preview: PublicHandoffPreview }
	| { success: false; status: "not-found" }
	| { success: false; status: "error"; message: string };

export async function getPublicHandoff(
	token: string,
): Promise<PublicHandoffResult> {
	try {
		const response = await apiClient.get<{ data: PublicHandoffPreview }>(
			`${API_BASE}/buyer-handoffs/token/${encodeURIComponent(token)}`,
		);
		return { success: true, preview: response.data.data };
	} catch (error: unknown) {
		const err = error as AxiosLikeError;
		if (err.response?.status === 404) {
			return { success: false, status: "not-found" };
		}
		if (!err.response) {
			return {
				success: false,
				status: "error",
				message:
					err.code === "ECONNABORTED" || err.message?.includes("timeout")
						? "Request timed out. Check your connection and try again."
						: "Couldn't reach the server. Check your connection and try again.",
			};
		}
		return {
			success: false,
			status: "error",
			message: err.response.data?.message ?? "Couldn't load this link.",
		};
	}
}

/**
 * Token-scoped result mirrors the getPublicHandoff shape so the
 * claim page can render the same not-found UI whether the token was
 * invalid at preview time, send-otp time, or claim time. The backend
 * returns 404 for any miss; we map it to a stable union here.
 */
export type TokenScopedResult<T> =
	| { success: true; data: T }
	| { success: false; status: "not-found" }
	| { success: false; status: "error"; message: string };

function buildTokenFailure<T>(
	error: unknown,
	fallback: string,
): TokenScopedResult<T> {
	const err = error as AxiosLikeError;
	if (err?.response?.status === 404) {
		return { success: false, status: "not-found" };
	}
	if (!err?.response) {
		return {
			success: false,
			status: "error",
			message:
				err?.code === "ECONNABORTED" || err?.message?.includes("timeout")
					? "Request timed out. Check your connection and try again."
					: "Couldn't reach the server. Check your connection and try again.",
		};
	}
	return {
		success: false,
		status: "error",
		message: err.response.data?.message ?? fallback,
	};
}

export async function sendClaimOtp(
	token: string,
): Promise<TokenScopedResult<{ buyerPhoneMasked: string }>> {
	try {
		const response = await apiClient.post<{
			data: { buyerPhoneMasked: string };
		}>(
			`${API_BASE}/buyer-handoffs/token/${encodeURIComponent(token)}/send-otp`,
		);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildTokenFailure(
			error,
			"Couldn't send OTP. Try again in a moment.",
		);
	}
}

export interface ClaimHandoffInput {
	otp: string;
	homeownerName: string;
}

/**
 * Public-safe user shape the backend returns on successful claim
 * (matches PublicClaimUser server-side). We deliberately don't
 * reuse FinishdUser here — the claim endpoint hands back a minimal
 * projection, and the frontend hydrates the full user via
 * getCurrentUser() right after.
 */
export interface ClaimHandoffUser {
	id: string;
	phone: string;
	userType: string | null;
	languagePreference: string | null;
}

export interface ClaimHandoffResult {
	user: ClaimHandoffUser;
	tokens: { accessToken: string; refreshToken: string };
	// The backend echoes the id it labelled the new session with.
	deviceId: string;
	handoffId: string;
	propertyId: string;
}

export async function claimHandoff(
	token: string,
	input: ClaimHandoffInput,
): Promise<TokenScopedResult<ClaimHandoffResult>> {
	try {
		const response = await apiClient.post<{ data: ClaimHandoffResult }>(
			`${API_BASE}/buyer-handoffs/token/${encodeURIComponent(token)}/claim`,
			// Stable per-install id so the session created on claim labels this
			// device rather than a new one each claim.
			{ ...input, deviceId: getOrCreateDeviceId() },
		);
		// Keep the id the backend actually stored, not the one we generated.
		if (response.data.data?.deviceId) {
			setDeviceId(response.data.data.deviceId);
		}
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildTokenFailure<ClaimHandoffResult>(
			error,
			"Couldn't complete onboarding. Please try again.",
		);
	}
}
